// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { Col, Layout, Row } from 'antd';

import { useTranslation } from 'react-i18next';
import { LoginComponent } from '../../components/login/LoginComponent';
import Separator from '../../components/separator/Separator';
import './TenantLogin.less';
import { lookupTenant } from '../../api/tenantLookupApi';
import { getIDPUrl } from '../../utils';
import { v4 as uuidv4 } from 'uuid';
import { useCallback } from 'react';
import { useNotifications } from '../../errorHandler/context/Notifications';
import { AppError } from '../../errorHandler/errors';
import { ErrorCode } from '../../errorHandler/constants';

const TenantLogin = () => {
  const { t } = useTranslation();
  const { emitError } = useNotifications();

  const onLogin = useCallback(
    async (email: string) => {
      try {
        const response = await lookupTenant(email);
        // User is redirected to lookup only when tenantId is not known.
        const tenantList = response?.tenantList ?? [];
        if (tenantList.length === 0) {
          emitError(new AppError({ code: ErrorCode.TENANT_NOT_FOUND }));
          return;
        }

        const workflowId = uuidv4();

        const selectedTenant =
          tenantList.length === 1
            ? tenantList[0]
            : tenantList.find(t => t.tenantType === 'PRIMARY');

        if (selectedTenant) {
          window.location.href = getIDPUrl(selectedTenant.tenantId, workflowId);
        } else {
          emitError(new AppError({ code: ErrorCode.TENANT_NOT_FOUND }));
        }
      } catch (error) {
        console.error('Error during tenant lookup:', error);
        emitError(new AppError({ code: ErrorCode.TENANT_NOT_FOUND }));
      }
    },
    [emitError],
  );

  const { Content } = Layout;
  return (
    <Layout className={'tenant-lookup-window'}>
      <Content>
        <Row className="tenant-lookup-container">
          <Col xs={0} sm={0} md={12} lg={14} className="portal-info">
            <div className="tenant-lookup-info-steps">
              <h1>{t('tenant-lookup.info.title')}</h1>
              <Separator />
              <div>
                <p>{t('tenant-lookup.info.steps-1')}</p>
                <p>{t('tenant-lookup.info.steps-2')}</p>
              </div>
            </div>
            <TenantLookupFooter />
          </Col>
          <Col xs={24} sm={24} md={12} lg={10}>
            <LoginComponent onSubmit={onLogin} />
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export { TenantLogin };

const TenantLookupFooter = () => {
  return (
    <div className={'tenant-lookup-info-footer'}>
      <p>© {new Date().getFullYear()} Imprivata, Inc. All rights reserved.</p>
    </div>
  );
};
